import { useEffect, useMemo, useState } from "react";
import usePersistentPageState from "../hooks/usePersistentPageState";
import { BASE_URL } from "../config";
import { showSuccess, showError } from "../utils/dialog";
import Layout from "./Layout";
import { useAppPermissions } from "../context/PermissionContext";
import { formatAppDateTime } from "../utils/appDateTime";

const statusColors = {
  Pending: "bg-amber-100 text-amber-800",
  Approved: "bg-green-100 text-green-800",
  Rejected: "bg-red-100 text-red-800",
  Conflict: "bg-fuchsia-100 text-fuchsia-800",
};

export default function AdminTimesheets() {
  const { canWriteAdmin } = useAppPermissions();
  const adminReadOnly = !canWriteAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = usePersistentPageState("page_search_admin_timesheets", "");
  const [status, setStatus] = usePersistentPageState("page_status_admin_timesheets", "");
  const [client, setClient] = usePersistentPageState("page_client_admin_timesheets", "");

  useEffect(() => {
    const controller = new AbortController();
    async function load() {
      setLoading(true);
      try {
        const qs = new URLSearchParams({ search, status, client });
        const res = await fetch(`${BASE_URL}/api/admin/timesheets?${qs.toString()}`, {
          signal: controller.signal,
        });
        const json = await res.json();
        setRows(json.data || []);
      } catch (e) {
        if (e.name !== "AbortError") console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => controller.abort();
  }, [search, status, client]);

  const onApproveAll = async () => {
    // mock
    showSuccess("Approved all visible timesheets (mock)");
  };
  const onRejectAll = async () => {
    showError("Rejected all visible timesheets (mock)");
  };

  return (
    <Layout active="Admin">
      <fieldset
        disabled={adminReadOnly}
        className="border-0 m-0 p-0 min-w-0 min-h-0"
      >
      <div className="p-4">
        <div className="sticky top-0 bg-white z-10 border-b mb-4">
          <div className="flex items-center gap-3 py-3">
            {/* <input
              className="border rounded px-3 py-2 w-60"
              placeholder="Search staff/client/position"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            /> */}

            {/* <button onClick={() => window.open('/admin/timesheets/print', '_blank')} className="px-3 py-2 rounded border">Print</button> */}
            {/* <select className="border rounded px-2 py-2" value={status} onChange={(e) => setStatus(e.target.value)}>
							<option value="">All Status</option>
							<option>Pending</option>
							<option>Approved</option>
							<option>Rejected</option>
							<option>Conflict</option>
						</select> */}
            {/* <input
							className="border rounded px-3 py-2 w-48"
							placeholder="Client"
							value={client}
							onChange={(e) => setClient(e.target.value)}
						/> */}
            {/* <div className="ml-auto flex gap-2">
							<button onClick={onRejectAll} className="px-3 py-2 rounded bg-red-600 text-white">Reject All</button>
							<button onClick={onApproveAll} className="px-3 py-2 rounded bg-green-600 text-white">Approve All</button>
							
						</div> */}
          </div>
        </div>

        <div className="border rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-3 py-2">Staff..</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Position</th>
                <th className="px-3 py-2">Scheduled</th>
                <th className="px-3 py-2">Actual</th>
                <th className="px-3 py-2">Duration</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center">
                    No results
                  </td>
                </tr>
              )}
              {rows.map((r, idx) => (
                <tr key={idx} className="border-t">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold">
                        {(r.staffName || "?")
                          .split(" ")
                          .map((s) => s[0])
                          .join("")}
                      </div>
                      <div className="leading-tight">
                        <div className="font-medium">{r.staffName}</div>
                        <div className="text-xs text-slate-500">
                          ID #{r.staffId}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">{r.client}</td>
                  <td className="px-3 py-2">{r.position}</td>
                  <td className="px-3 py-2">
                    <div className="text-xs text-slate-600">
                      {formatAppDateTime(r.scheduledStart)}
                    </div>
                    <div className="text-xs text-slate-600">
                      {formatAppDateTime(r.scheduledEnd)}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-xs text-slate-600">
                      {formatAppDateTime(r.actualStart)}
                    </div>
                    <div className="text-xs text-slate-600">
                      {formatAppDateTime(r.actualEnd)}
                    </div>
                  </td>
                  <td className="px-3 py-2">{r.duration}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        statusColors[r.status] || "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2">
                      <button className="px-2 py-1 text-xs rounded border">
                        Details
                      </button>
                      <button className="px-2 py-1 text-xs rounded border">
                        Edit
                      </button>
                      <button className="px-2 py-1 text-xs rounded border">
                        Approve
                      </button>
                      <button className="px-2 py-1 text-xs rounded border">
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </fieldset>
    </Layout>
  );
}
